import PropTypes from "prop-types";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  HeartIcon,
  LocationMarkerIcon,
  MailIcon,
  PhoneIcon,
  ShieldCheckIcon,
  StarIcon,
} from "@heroicons/react/solid";
import { BACKEND_URL } from "../config";
import { getActiveOffers } from "../utils/offerUtils";

function Pill({ children, tone = "slate" }) {
  const styles = {
    emerald: "border-emerald-300/30 bg-emerald-300/10 text-emerald-100",
    cyan: "border-cyan-300/30 bg-cyan-300/10 text-cyan-100",
    amber: "border-amber-300/30 bg-amber-300/10 text-amber-100",
    slate: "border-white/12 bg-white/[0.07] text-slate-200",
  };

  return (
    <span className={`inline-flex items-center rounded-md border px-2 py-1 text-[11px] font-semibold leading-none ${styles[tone] || styles.slate}`}>
      {children}
    </span>
  );
}

function getTrustState(consultancy) {
  if (["ADMIN_VERIFIED", "OWNER_VERIFIED"].includes(consultancy.verificationLevel)) {
    return { label: "Verified", tone: "emerald" };
  }
  if (consultancy.claimed || consultancy.claimStatus === "VERIFIED") {
    return { label: "Claimed", tone: "cyan" };
  }
  if (consultancy.published || ["PUBLISHED", "VERIFIED"].includes(consultancy.verificationStatus)) {
    return { label: "Source confirmed", tone: "cyan" };
  }
  return { label: "Review pending", tone: "slate" };
}

function normalizedArray(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  return value ? [value] : [];
}

export default function ConsultancyCard({
  consultancy = {},
  variant = "default",
  onView,
  onContact,
}) {
  const navigate = useNavigate();
  const user = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const activeOffers = getActiveOffers(consultancy.offers || []);
  const topOffer = consultancy.topOffer || activeOffers[0] || null;
  const otherOffersCount = topOffer ? Math.max(0, activeOffers.length - 1) : 0;
  const countries = normalizedArray(consultancy.countriesServed || consultancy.countries).slice(0, 4);
  const services = normalizedArray(consultancy.services || consultancy.courses).slice(0, 3);
  const destinationSummary = countries.length ? `${countries.length} destination${countries.length === 1 ? "" : "s"}` : "Not listed";
  const serviceSummary = services.length ? `${services.length} service${services.length === 1 ? "" : "s"}` : "Not listed";
  const trustState = getTrustState(consultancy);
  const rating = Number(consultancy.googleRating || consultancy.rating || 0);
  const reviewCount = Number(consultancy.googleReviewCount || 0);
  const hasSourcedRating = rating > 0 && reviewCount > 0;
  const location = [consultancy.city, consultancy.state].filter(Boolean).join(", ") || consultancy.location || "Location not listed";
  const phone = consultancy.phone || consultancy.contactNumber || "";
  const email = consultancy.email || "";
  const compact = variant === "compact";
  const mediaBaseUrl = BACKEND_URL.replace(/\/api$/, "");
  const logoSrc = consultancy.logo
    ? consultancy.logo.startsWith("http")
      ? consultancy.logo
      : `${mediaBaseUrl}${consultancy.logo}`
    : "";

  const handleContact = (event) => {
    event.stopPropagation();
    if (!user) {
      navigate("/login");
      return;
    }
    if (onContact) {
      onContact();
      return;
    }
    if (email) window.location.href = `mailto:${email}`;
  };

  return (
    <article
      className={`group rounded-lg border border-white/10 bg-white/[0.05] text-white shadow-sm transition hover:-translate-y-0.5 hover:border-cyan-300/30 hover:bg-white/[0.07] hover:shadow-xl ${
        compact ? "p-3.5" : "p-3.5 sm:p-4"
      }`}
    >
      <div className="flex gap-3">
        <div className="flex h-11 w-11 flex-none items-center justify-center rounded-lg border border-white/10 bg-slate-950/75 text-sm font-bold uppercase text-cyan-100">
          {logoSrc ? (
            <img src={logoSrc} alt={`${consultancy.name || "Consultancy"} logo`} className="h-full w-full rounded-lg object-cover" loading="lazy" decoding="async" />
          ) : (
            consultancy.name?.slice(0, 2) || "AJ"
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Pill tone={trustState.tone}>
              <ShieldCheckIcon className="mr-1 h-3.5 w-3.5" />
              {trustState.label}
            </Pill>
            {consultancy.featured && <Pill tone="amber">Featured</Pill>}
            {topOffer && <Pill tone="cyan">Offer live</Pill>}
          </div>
          <h3 className="mt-2 line-clamp-2 text-base font-semibold leading-snug text-white">
            {consultancy.name || "Unnamed consultancy"}
          </h3>
          <p className="mt-1.5 flex items-center gap-1.5 text-xs text-slate-300">
            <LocationMarkerIcon className="h-3.5 w-3.5 flex-none text-slate-500" />
            <span className="truncate">{location}</span>
          </p>
        </div>

        <button
          type="button"
          aria-label="Save consultancy"
          onClick={(event) => {
            event.stopPropagation();
            navigate(user ? "/wishlist" : "/login");
          }}
          className="flex h-11 w-11 flex-none items-center justify-center rounded-lg border border-white/10 bg-white/[0.055] text-slate-300 transition hover:border-rose-300/30 hover:text-rose-200 focus:outline-none focus:ring-2 focus:ring-rose-300/30 sm:h-9 sm:w-9"
        >
          <HeartIcon className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-3 grid gap-2 min-[420px]:grid-cols-3">
        <div className="rounded-md border border-white/10 bg-slate-950/55 p-2.5">
          <p className="text-xs text-slate-500">{hasSourcedRating ? "Directory rating" : "Profile score"}</p>
          <p className="mt-0.5 flex items-center gap-1 text-sm font-semibold">
            {hasSourcedRating && <StarIcon className="h-3.5 w-3.5 text-amber-300" />}
            {hasSourcedRating ? rating.toFixed(1) : `${consultancy.dataCompletenessScore || 0}/100`}
          </p>
          <p className="text-xs text-slate-500">{hasSourcedRating ? `${reviewCount} reviews` : "Completeness"}</p>
        </div>
        <div className="rounded-md border border-white/10 bg-slate-950/55 p-2.5">
          <p className="text-xs text-slate-500">Destinations</p>
          <p className="mt-0.5 text-sm font-semibold">{destinationSummary}</p>
          <p className="truncate text-xs text-slate-500">{countries.join(", ") || "Ask counsellor"}</p>
        </div>
        <div className="rounded-md border border-white/10 bg-slate-950/55 p-2.5">
          <p className="text-xs text-slate-500">Services</p>
          <p className="mt-0.5 text-sm font-semibold">{serviceSummary}</p>
          <p className="truncate text-xs text-slate-500">{services.join(", ") || "Counselling"}</p>
        </div>
      </div>

      {topOffer && (
        <div className="mt-3 rounded-md border border-amber-300/20 bg-amber-300/10 p-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[1.5px] text-amber-100/80">Current offer</p>
              <p className="mt-1 line-clamp-1 text-sm font-semibold text-white">{topOffer.title || "Student offer"}</p>
              {topOffer.description && <p className="mt-1 line-clamp-1 text-xs text-amber-50/80">{topOffer.description}</p>}
            </div>
            {otherOffersCount > 0 && <Pill tone="amber">+{otherOffersCount}</Pill>}
          </div>
        </div>
      )}

      <div className="mt-3 rounded-md border border-white/10 bg-slate-950/45 p-2.5">
        {user ? (
          <div className="grid gap-1.5 text-xs text-slate-300">
            <p className="flex min-w-0 items-center gap-2">
              <MailIcon className="h-3.5 w-3.5 flex-none text-slate-500" />
              <span className="truncate">{email || "Email not listed"}</span>
            </p>
            <p className="flex min-w-0 items-center gap-2">
              <PhoneIcon className="h-3.5 w-3.5 flex-none text-slate-500" />
              <span className="truncate">{phone || "Phone not listed"}</span>
            </p>
          </div>
        ) : (
          <p className="text-xs leading-5 text-slate-300">Login to reveal phone/email and send enquiries securely.</p>
        )}
      </div>

      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        <button
          type="button"
          onClick={onView}
          className="min-h-[44px] flex-1 rounded-lg bg-white px-3 py-2.5 text-sm font-semibold text-slate-950 transition hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-white/70"
        >
          View profile
        </button>
        <button
          type="button"
          onClick={handleContact}
          className="min-h-[44px] flex-1 rounded-lg border border-white/12 bg-white/[0.055] px-3 py-2.5 text-sm font-semibold text-white transition hover:bg-white/[0.09] focus:outline-none focus:ring-2 focus:ring-cyan-300/35"
        >
          {user ? "Get counselling" : "Login to contact"}
        </button>
      </div>
    </article>
  );
}

ConsultancyCard.propTypes = {
  consultancy: PropTypes.object,
  variant: PropTypes.oneOf(["default", "compact"]),
  onView: PropTypes.func,
  onContact: PropTypes.func,
};
